import type { ApiEventDay } from 'leLive/api/leLive'
import appConfig from 'constants/config'
import { getCurrentIntl } from 'i18n/util/currentIntl'

const EVENT_URL = `${appConfig.SCHEME}://${appConfig.CUSTOMER_PORTAL_HOST}/au/live`

interface DayConfig {
  startDate: string
  endDate: string
  startTime: string
  endTime: string
}

// AEST = UTC+10. Event runs 10am–6pm each day.
// BOTH uses all-day format with exclusive end date (RFC 5545).
const DAY_CONFIG: Record<ApiEventDay | 'BOTH', DayConfig> = {
  DAY_ONE: {
    startDate: '20260516',
    endDate: '20260516',
    startTime: 'T100000',
    endTime: 'T180000',
  },
  DAY_TWO: {
    startDate: '20260517',
    endDate: '20260517',
    startTime: 'T100000',
    endTime: 'T180000',
  },
  BOTH: {
    startDate: '20260516',
    endDate: '20260518',
    startTime: '',
    endTime: '',
  },
}

function getEventCalendarDetails() {
  const intl = getCurrentIntl()

  return {
    eventName: intl.formatMessage(
      {
        id: '5hwyW05-owmh-jY6DkRy',
        defaultMessage: '{brandName} Live 2026',
        description: 'Event title used in exported calendar files and calendar links for the LE Live landing page',
      },
      { brandName: appConfig.brandConfig.name },
    ),
    eventLocation: intl.formatMessage({
      id: 'iAl6-JPILcUvJiYUUCz4',
      defaultMessage: 'Royal Exhibition Building, 9 Nicholson St, Carlton VIC 3053',
      description: 'Event venue and address used in exported calendar files and calendar links for LE Live',
    }),
    eventDescription: intl.formatMessage(
      {
        id: 'bS-ghD24d3AHz0ei7oNX',
        defaultMessage: 'Australia’s ultimate luxury travel event.\n{eventUrl}',
        description: 'Event description used in exported calendar files and calendar links for LE Live',
      },
      { eventUrl: EVENT_URL },
    ),
  }
}

function getCalendarLanguageTag() {
  return getCurrentIntl().locale.split('-')[0]?.toUpperCase() || 'EN'
}

function getCalendarFileSafeSlug(value: string) {
  return value
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-zA-Z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .toLowerCase()
}

export function getCalendarDownloadFilename() {
  return `${getCalendarFileSafeSlug(getEventCalendarDetails().eventName)}.ics`
}

export function getIcalContent(eventDay?: ApiEventDay): string {
  const dayConfig = eventDay ? DAY_CONFIG[eventDay] : DAY_CONFIG.BOTH
  const isAllDay = !dayConfig.startTime
  const { eventDescription, eventLocation, eventName } = getEventCalendarDetails()

  const dtStart = isAllDay
    ? `DTSTART;VALUE=DATE:${dayConfig.startDate}`
    : `DTSTART;TZID=Australia/Melbourne:${dayConfig.startDate}${dayConfig.startTime}`
  const dtEnd = isAllDay
    ? `DTEND;VALUE=DATE:${dayConfig.endDate}`
    : `DTEND;TZID=Australia/Melbourne:${dayConfig.endDate}${dayConfig.endTime}`

  return [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    `PRODID:-//${appConfig.brandConfig.name}//${eventName}//${getCalendarLanguageTag()}`,
    'BEGIN:VEVENT',
    `UID:le-live-2026-${eventDay?.toLowerCase() ?? 'both'}@${appConfig.CUSTOMER_PORTAL_HOST}`,
    dtStart,
    dtEnd,
    `SUMMARY:${eventName}`,
    `DESCRIPTION:${eventDescription.replace(/\n/g, '\\n')}`,
    `LOCATION:${eventLocation.replace(/,/g, '\\,')}`,
    `URL:${EVENT_URL}`,
    'END:VEVENT',
    'END:VCALENDAR',
  ].join('\r\n')
}

export function getGoogleCalendarUrl(eventDay?: ApiEventDay): string {
  const dayConfig = eventDay ? DAY_CONFIG[eventDay] : DAY_CONFIG.BOTH
  const { eventDescription, eventLocation, eventName } = getEventCalendarDetails()
  const dates = dayConfig.startTime
    ? `${dayConfig.startDate}${dayConfig.startTime}/${dayConfig.endDate}${dayConfig.endTime}`
    : `${dayConfig.startDate}/${dayConfig.endDate}`

  return 'https://calendar.google.com/calendar/render?' + new URLSearchParams({
    action: 'TEMPLATE',
    text: eventName,
    dates,
    details: eventDescription,
    location: eventLocation,
    ...(dayConfig.startTime ? { ctz: 'Australia/Melbourne' } : {}),
  }).toString()
}

export function getOutlookCalendarUrl(eventDay?: ApiEventDay): string {
  const dayConfig = eventDay ? DAY_CONFIG[eventDay] : DAY_CONFIG.BOTH
  const isAllDay = !dayConfig.startTime
  const { eventDescription, eventLocation, eventName } = getEventCalendarDetails()

  const startdt = `${dayConfig.startDate.slice(0, 4)}-${dayConfig.startDate.slice(4, 6)}-${dayConfig.startDate.slice(6, 8)}`
  const enddt = `${dayConfig.endDate.slice(0, 4)}-${dayConfig.endDate.slice(4, 6)}-${dayConfig.endDate.slice(6, 8)}`

  return 'https://outlook.live.com/calendar/0/action/compose?' + new URLSearchParams({
    subject: eventName,
    startdt: isAllDay ? startdt : `${startdt}T${dayConfig.startTime.slice(1, 3)}:${dayConfig.startTime.slice(3, 5)}:${dayConfig.startTime.slice(5, 7)}`,
    enddt: isAllDay ? enddt : `${enddt}T${dayConfig.endTime.slice(1, 3)}:${dayConfig.endTime.slice(3, 5)}:${dayConfig.endTime.slice(5, 7)}`,
    body: eventDescription,
    location: eventLocation,
    ...(isAllDay ? { allday: 'true' } : {}),
    path: '/calendar/action/compose',
  }).toString()
}
